// notificationSound — the user's in-app notification sound. Predefined sounds are
// synthesized with the Web Audio API (no audio files to ship/host); a custom sound is
// an uploaded mp3 stored as a data URL in localStorage. Preference is per-device.

const KEY = "tmc.notifSound";
export const MAX_CUSTOM_BYTES = 512 * 1024; // ~500 KB — keeps the data URL inside localStorage limits

export const SOUNDS = [
  { id: "chime", name: "Chime" },
  { id: "ping", name: "Ping" },
  { id: "pop", name: "Pop" },
  { id: "knock", name: "Knock" },
  { id: "ring", name: "Ring" },
  { id: "none", name: "Silent" },
];

let ctx;
function audioCtx() {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
}

// One enveloped oscillator note.
function tone(ac, { freq, start, dur, type = "sine", gain = 0.2 }) {
  const t0 = ac.currentTime + start;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.03);
}

const SYNTH = {
  chime: (ac) => { tone(ac, { freq: 660, start: 0, dur: 0.18 }); tone(ac, { freq: 988, start: 0.12, dur: 0.3 }); },
  ping:  (ac) => { tone(ac, { freq: 880, start: 0, dur: 0.24 }); },
  pop:   (ac) => { tone(ac, { freq: 440, start: 0, dur: 0.08, type: "triangle", gain: 0.28 }); tone(ac, { freq: 720, start: 0.05, dur: 0.11, type: "triangle", gain: 0.24 }); },
  knock: (ac) => { tone(ac, { freq: 200, start: 0, dur: 0.1, type: "square", gain: 0.16 }); tone(ac, { freq: 180, start: 0.15, dur: 0.12, type: "square", gain: 0.16 }); },
  // Classic telephone ring: two bursts of a 440+480 Hz pair.
  ring: (ac) => {
    for (const start of [0, 0.6]) {
      tone(ac, { freq: 480, start, dur: 0.4, type: "sine", gain: 0.22 });
      tone(ac, { freq: 440, start, dur: 0.4, type: "sine", gain: 0.16 });
    }
  },
};

// A sound preference has one per KIND: "message" (new messages) and "mention".
const DEFAULTS = {
  message: { id: "chime", custom: null },
  mention: { id: "ping", custom: null },
};
const norm = (p, def) => (p && typeof p.id === "string" ? { id: p.id, custom: p.custom || null } : { ...def });

export function getSoundPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY));
    if (raw && (raw.message || raw.mention))
      return { message: norm(raw.message, DEFAULTS.message), mention: norm(raw.mention, DEFAULTS.mention) };
    if (raw && typeof raw.id === "string") { // migrate the old single preference → message + mention
      const p = { id: raw.id, custom: raw.custom || null };
      return { message: p, mention: { ...p } };
    }
  } catch { /* ignore */ }
  return { message: { ...DEFAULTS.message }, mention: { ...DEFAULTS.mention } };
}

export function getSoundPref(kind) {
  return getSoundPrefs()[kind] || { ...DEFAULTS.message };
}

export function setSoundPref(kind, pref) {
  const all = getSoundPrefs();
  all[kind] = { id: pref.id, custom: pref.custom || null };
  try { localStorage.setItem(KEY, JSON.stringify(all)); } catch { /* private mode / quota */ }
}

// Play a specific sound. `custom` is the mp3 data URL when id === "custom".
export function playSound(id, custom) {
  try {
    if (id === "none") return;
    if (id === "custom") {
      if (!custom) return;
      const a = new Audio(custom);
      a.volume = 0.6;
      a.play().catch(() => { /* autoplay blocked until a user gesture */ });
      return;
    }
    (SYNTH[id] || SYNTH.chime)(audioCtx());
  } catch { /* audio unavailable */ }
}

// Play the user's chosen sound for a kind ("message" | "mention") when a notification arrives.
export function playNotification(kind) {
  const p = getSoundPref(kind);
  playSound(p.id, p.custom);
}
